<template>
  <AdminShell padded>
    <a-spin :spinning="listLoading">
      <a-empty v-if="!listLoading && !displayItems.length" description="暂无数据" />

      <div v-else class="crawl-card-grid">
        <article
          v-for="item in displayItems"
          :key="item.sourceId"
          class="crawl-card"
          @click="openEdit(item)"
        >
          <div class="crawl-card-head">
            <div class="crawl-card-titles">
              <h3 class="crawl-card-title">{{ item.website_name }}</h3>
              <p v-if="cardSubtitle(item)" class="crawl-card-sub">{{ cardSubtitle(item) }}</p>
            </div>
            <a-button type="link" size="small" @click.stop="openEdit(item)">编辑</a-button>
          </div>
        </article>
      </div>
    </a-spin>

    <a-drawer
      v-model:open="editOpen"
      :title="editing?.website_name || '编辑'"
      width="480"
      destroy-on-close
      @close="resetEdit"
    >
      <a-spin :spinning="detailLoading">
        <template v-if="editing">
          <p class="adm-intro-row">
            <span class="adm-intro-label">网站</span>{{ editing.website_name }}
          </p>

          <a-form layout="vertical" class="adm-source-form">
            <a-form-item v-if="editMeta.showKeywords" :label="editMeta.keywordsLabel">
              <a-textarea
                v-model:value="keywordsText"
                :rows="10"
                :placeholder="editMeta.keywordsPlaceholder || '每行一个'"
              />
            </a-form-item>

            <a-empty
              v-if="!editMeta.showKeywords"
              description="暂无可编辑项（该源爬虫不消费运营关键词）"
            />
          </a-form>
        </template>
      </a-spin>

      <template #footer>
        <a-space>
          <a-button @click="editOpen = false">取消</a-button>
          <a-button type="primary" :loading="saving" :disabled="detailLoading" @click="saveEdit">
            保存
          </a-button>
        </a-space>
      </template>
    </a-drawer>
  </AdminShell>
</template>

<script>
import { message } from 'ant-design-vue'
import AdminShell from '@/components/admin/AdminShell.vue'
import { getDataSourceList, getDataSourceDetail, saveCrawlParams } from '@/api/adminApi'
import { SOURCE_LIST_ITEMS } from '@/data/sourceConfigCatalog'
import {
  getCrawlFieldMeta,
  extractKeywords,
  keywordsToText,
  mergeEditableParams,
  resolveChannelConfig,
} from '@/utils/crawlParamsBuilder'

export default {
  name: 'CrawlConfigView',
  components: { AdminShell },
  data() {
    return {
      listLoading: false,
      listItems: [],
      editOpen: false,
      detailLoading: false,
      saving: false,
      editing: null,
      editMeta: {},
      parentDatasourceId: '',
      channelConfig: {},
      keywordsText: '',
      metaCache: {},
    }
  },
  computed: {
    /** 只展示爬虫会实际消费运营关键词的数据源 */
    displayItems() {
      return this.listItems.filter(
        (item) => this.fieldMeta(item.sourceId).hasEditableOps,
      )
    },
  },
  mounted() {
    this.loadList()
  },
  methods: {
    fieldMeta(sourceId) {
      if (!this.metaCache[sourceId]) {
        this.metaCache[sourceId] = getCrawlFieldMeta(sourceId)
      }
      return this.metaCache[sourceId]
    },
    /** 千里马 / WHO / FDA / PR Newswire 卡片显示子标题 */
    cardSubtitle(item) {
      const id = String(item?.sourceId || '')
      const name = String(item?.website_name || '')
      if (/^qianlima_/i.test(id) || name.includes('千里马')) {
        return id.replace(/^qianlima_/i, '')
      }
      if (/^who_ictrp/i.test(id) || name.includes('WHO临床实验登记')) {
        return id.replace(/^who_/i, '')
      }
      if (/^prnewswire/i.test(id) || name.includes('PR Newswire新闻')) {
        return id.replace(/^prnewswire_/i, '')
      }
      if (/^fdadevice_/i.test(id)) {
        return id.replace(/^fdadevice_/i, '')
      }
      return ''
    },
    normalizeListRows(data) {
      const rows = Array.isArray(data) ? data : []
      return rows.map((row) => ({
        sourceId: row.datasource_id,
        website_name: row.website_name || row.datasource_id,
      }))
    },
    /**
     * 后端 getDataSourceList 可能尚未登记新源（如 fdadevice_pma）
     * 用 catalog 中「有可编辑运营参数」的源补全，保证运营可配入口可见
     */
    mergeCatalogEditableSources(rows) {
      const byId = new Map()
      for (const row of rows) {
        if (row?.sourceId) byId.set(row.sourceId, row)
      }
      for (const item of SOURCE_LIST_ITEMS) {
        const id = item.sourceId
        if (!id || byId.has(id)) continue
        if (!getCrawlFieldMeta(id).hasEditableOps) continue
        byId.set(id, {
          sourceId: id,
          website_name: item.siteName || item.name || id,
          _fromCatalog: true,
        })
      }
      return Array.from(byId.values())
    },
    async loadList() {
      this.listLoading = true
      try {
        const data = await getDataSourceList()
        this.listItems = this.mergeCatalogEditableSources(this.normalizeListRows(data))
        this.metaCache = {}
      } catch (err) {
        // 列表接口失败时仍展示 catalog 可编辑源，避免新源完全不可见
        this.listItems = this.mergeCatalogEditableSources([])
        message.error(err?.response?.data?.detail || '加载数据源列表失败（已回退本地目录）')
      } finally {
        this.listLoading = false
      }
    },
    fillFormFromConfig(config, meta) {
      this.keywordsText = keywordsToText(extractKeywords(config, meta.keywordsPath, this.editing?.sourceId))
    },
    async openEdit(record) {
      if (!record?.sourceId) return

      this.editing = record
      this.editOpen = true
      this.detailLoading = true
      this.parentDatasourceId = ''
      this.channelConfig = {}
      this.keywordsText = ''
      this.editMeta = getCrawlFieldMeta(record.sourceId)
      this.metaCache[record.sourceId] = this.editMeta

      try {
        const detail = await getDataSourceDetail(record.sourceId)

        if (detail?.config && typeof detail.config === 'object') {
          // 详情若带回父级 datasource_id 则用之，否则用列表项 ID
          this.parentDatasourceId = detail.datasource_id || record.sourceId
          this.channelConfig = resolveChannelConfig(record.sourceId, detail.config)
        } else {
          this.parentDatasourceId = record.sourceId
          this.channelConfig = resolveChannelConfig(record.sourceId, null)
        }
      } catch (err) {
        message.error(err?.response?.data?.detail || err.message || '加载配置详情失败')
        this.parentDatasourceId = record.sourceId
        this.channelConfig = resolveChannelConfig(record.sourceId, null)
      } finally {
        this.detailLoading = false
      }

      this.fillFormFromConfig(this.channelConfig, this.editMeta)
    },
    resetEdit() {
      this.editing = null
      this.editMeta = {}
      this.parentDatasourceId = ''
      this.channelConfig = {}
      this.keywordsText = ''
    },
    async saveEdit() {
      if (!this.editing?.sourceId) return

      const updatedConfig = mergeEditableParams(this.channelConfig, this.editMeta, {
        keywordsText: this.keywordsText,
      })

      const parentId = this.parentDatasourceId || this.editing.sourceId
      if (!parentId) {
        message.error('无法确定所属数据源')
        return
      }

      this.saving = true
      try {
        await saveCrawlParams({
          datasource_id: parentId,
          sourceid_business_data: {
            [this.editing.sourceId]: updatedConfig,
          },
        })
        message.success('保存成功')
        this.channelConfig = updatedConfig
        this.parentDatasourceId = parentId
        this.editOpen = false
      } catch (err) {
        message.error(err?.response?.data?.detail || err.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.crawl-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.crawl-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.crawl-card:hover {
  border-color: #d9d9d9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.crawl-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.crawl-card-titles {
  flex: 1;
  min-width: 0;
}

.crawl-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.4;
}

.crawl-card-sub {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.45);
  word-break: break-all;
}

.adm-intro-row {
  margin: 0 0 16px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
}

.adm-intro-label {
  display: inline-block;
  width: 48px;
  color: rgba(0, 0, 0, 0.45);
}

.adm-source-form :deep(.ant-form-item-label) {
  font-weight: 500;
}
</style>
